import React, { useState, useEffect } from 'react';
import { Shield, Plus, Download, Users, Activity, Settings, Key, Globe, Trash2, Eye, EyeOff, Copy, CheckCircle, AlertTriangle, Server, RefreshCw } from 'lucide-react';
import { toast } from 'react-toastify';

const VPNManagement = () => {
  const [vpnStatus, setVpnStatus] = useState(null);
  const [peers, setPeers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [peerNames, setPeerNames] = useState({}); // Store peer names locally

  const [showAddPeer, setShowAddPeer] = useState(false);
  const [newPeer, setNewPeer] = useState({ name: '', allowedIPs: '' });
  const [showPrivateKeys, setShowPrivateKeys] = useState({});
  const [addingPeer, setAddingPeer] = useState(false);
  const [deletingPeer, setDeletingPeer] = useState(null);
  const [restarting, setRestarting] = useState(false);

  // API functions
  const fetchVPNStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/vpn/status', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      const result = await response.json();
      if (result.success) {
        setVpnStatus(result.data);
      } else {
        setError(result.message);
      }
    } catch (err) {
      setError('Failed to fetch VPN status');
      console.error('Error fetching VPN status:', err);
    }
  };

  const fetchPeers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/vpn/peers', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      const result = await response.json();
      if (result.success) {
        // Assign names to peers based on stored names
        const peersWithNames = result.data.map(peer => ({
          ...peer,
          name: peerNames[peer.publicKey] || `Peer ${peer.id}`
        }));
        setPeers(peersWithNames);
      } else {
        setError(result.message);
      }
    } catch (err) {
      setError('Failed to fetch peers');
      console.error('Error fetching peers:', err);
    }
  };

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      await Promise.all([fetchVPNStatus(), fetchPeers()]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    
    // Auto-refresh every 30 seconds
    const interval = setInterval(loadData, 30000);
    return () => clearInterval(interval);
  }, [peerNames]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'running': case 'connected': return 'text-green-400';
      case 'stopped': case 'inactive': return 'text-red-400';
      case 'pending': return 'text-yellow-400';
      default: return 'text-gray-400';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'running': case 'connected': return <CheckCircle className="w-4 h-4" />;
      case 'stopped': case 'inactive': return <AlertTriangle className="w-4 h-4" />;
      default: return <Activity className="w-4 h-4" />;
    }
  };

  const restartVPN = async () => {
    setRestarting(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/vpn/restart', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      const result = await response.json();
      if (result.success) {
        toast.success('VPN service restarted successfully');
        await loadData();
      } else {
        toast.error(result.message || 'Failed to restart VPN');
      }
    } catch (err) {
      toast.error('Failed to restart VPN service');
      console.error('Error restarting VPN:', err);
    } finally {
      setRestarting(false);
    }
  };

  const downloadPeerConfig = async (peer) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/vpn/peers/${encodeURIComponent(peer.publicKey)}/config`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = url;
        a.download = `${peer.name || 'peer'}-wg0.conf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        toast.success('Configuration downloaded');
      } else {
        toast.error('Failed to download configuration');
      }
    } catch (err) {
      toast.error('Failed to download peer configuration');
      console.error('Error downloading config:', err);
    }
  };

  const deletePeer = async (peer) => {
    if (!window.confirm(`Are you sure you want to delete peer "${peer.name}"?`)) {
      return;
    }
    
    setDeletingPeer(peer.publicKey);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/vpn/peers/${encodeURIComponent(peer.publicKey)}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      const result = await response.json();
      if (result.success) {
        toast.success('Peer deleted successfully');
        // Remove peer name from local storage
        const newPeerNames = { ...peerNames };
        delete newPeerNames[peer.publicKey];
        setPeerNames(newPeerNames);
        await loadData();
      } else {
        toast.error(result.message || 'Failed to delete peer');
      }
    } catch (err) {
      toast.error('Failed to delete peer');
      console.error('Error deleting peer:', err);
    } finally {
      setDeletingPeer(null);
    }
  };

  const handleAddPeer = async () => {
    if (!newPeer.name || !newPeer.allowedIPs) {
      toast.error('Please fill in all required fields');
      return;
    }

    setAddingPeer(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/vpn/peers', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: newPeer.name,
          allowedIPs: newPeer.allowedIPs
        })
      });
      
      const result = await response.json();
      if (result.success) {
        toast.success('Peer added successfully');
        
        // Store peer name for future reference
        setPeerNames(prev => ({
          ...prev,
          [result.data.publicKey]: result.data.name
        }));
        
        // Show configuration to user
        const configBlob = new Blob([result.data.clientConfig], { type: 'text/plain' });
        const url = window.URL.createObjectURL(configBlob);
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = url;
        a.download = `${result.data.name}-wg0.conf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        
        setNewPeer({ name: '', allowedIPs: '' });
        setShowAddPeer(false);
        await loadData();
      } else {
        toast.error(result.message || 'Failed to add peer');
      }
    } catch (err) {
      toast.error('Failed to add peer');
      console.error('Error adding peer:', err);
    } finally {
      setAddingPeer(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      toast.success('Copied to clipboard');
    }).catch(() => {
      toast.error('Failed to copy to clipboard');
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex items-center space-x-2 text-slate-400">
          <RefreshCw className="w-5 h-5 animate-spin" />
          <span>Loading VPN status...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-900/20 border border-red-700 rounded-lg p-6">
        <div className="flex items-center space-x-2 text-red-400">
          <AlertTriangle className="w-5 h-5" />
          <span className="font-medium">Error loading VPN data</span>
        </div>
        <p className="text-red-300 mt-2">{error}</p>
        <button 
          onClick={loadData}
          className="mt-4 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
        >
          Retry
        </button>
      </div>
    );
  }

  if (!vpnStatus) {
    return (
      <div className="bg-slate-800 border border-slate-700 rounded-lg p-6">
        <div className="text-slate-400 text-center">
          No VPN status data available
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* VPN Server Status */}
      <div className="bg-slate-800 border border-slate-700 rounded-lg shadow-sm">
        <div className="p-6 border-b border-slate-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <Shield className="w-6 h-6 text-blue-400 mr-3" />
              <div>
                <h2 className="text-xl font-semibold text-slate-200">WireGuard VPN Server</h2>
                <p className="text-slate-400 mt-1">Secure VPN tunnel management</p>
              </div>
            </div>
            <div className="flex space-x-2">
              <button 
                onClick={loadData}
                className="px-3 py-2 bg-slate-700 text-slate-200 rounded-md hover:bg-slate-600 flex items-center"
                disabled={loading}
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </button>
              <button 
                onClick={restartVPN}
                disabled={restarting}
                className="px-3 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 flex items-center disabled:opacity-50"
              >
                <Activity className={`w-4 h-4 mr-2 ${restarting ? 'animate-pulse' : ''}`} />
                {restarting ? 'Restarting...' : 'Restart'}
              </button>
            </div>
          </div>
        </div>

        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="bg-slate-900 border border-slate-600 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-slate-400 text-sm">Status</span>
                <div className={`flex items-center ${getStatusColor(vpnStatus.status)}`}>
                  {getStatusIcon(vpnStatus.status)}
                  <span className="ml-1 text-sm font-medium capitalize">{vpnStatus.status}</span>
                </div>
              </div>
            </div>

            <div className="bg-slate-900 border border-slate-600 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-slate-400 text-sm">Interface</span>
                <span className="text-slate-200 font-mono">{vpnStatus.interface}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-slate-400 text-sm">Port</span>
                <span className="text-slate-200 font-mono">{vpnStatus.port}</span>
              </div>
            </div>

            <div className="bg-slate-900 border border-slate-600 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-slate-400 text-sm">Server IP</span>
                <span className="text-slate-200 font-mono text-sm">{vpnStatus.serverIP}</span>
              </div>
            </div>

            <div className="bg-slate-900 border border-slate-600 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-slate-400 text-sm">Connected</span>
                <span className="text-green-400 font-semibold">{vpnStatus.connectedPeers || 0}</span>
              </div>
            </div>
          </div>

          {/* Server Public Key */}
          <div className="mt-6 bg-slate-900 border border-slate-600 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center">
                <Key className="w-4 h-4 text-blue-400 mr-2" />
                <span className="text-slate-200 font-medium">Server Public Key</span>
              </div>
              <button 
                onClick={() => copyToClipboard(vpnStatus.publicKey)}
                className="text-slate-400 hover:text-slate-200"
              >
                <Copy className="w-4 h-4" />
              </button>
            </div>
            <div className="bg-slate-800 rounded p-3 font-mono text-sm text-slate-300 break-all">
              {vpnStatus.publicKey}
            </div>
          </div>
        </div>
      </div>

      {/* Peer Management */}
      <div className="bg-slate-800 border border-slate-700 rounded-lg shadow-sm">
        <div className="p-6 border-b border-slate-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <Users className="w-6 h-6 text-blue-400 mr-3" />
              <div>
                <h2 className="text-xl font-semibold text-slate-200">VPN Peers</h2>
                <p className="text-slate-400 mt-1">Manage VPN client connections</p>
              </div>
            </div>
            <button 
              onClick={() => setShowAddPeer(true)}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Peer
            </button>
          </div>
        </div>

        <div className="p-6">
          <div className="space-y-4">
            {peers.map(peer => (
              <div key={peer.id} className="bg-slate-900 border border-slate-600 rounded-lg p-4">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-4">
                    <div>
                      <h3 className="font-medium text-slate-200">{peer.name}</h3>
                      <div className={`flex items-center mt-1 ${getStatusColor(peer.status)}`}>
                        {getStatusIcon(peer.status)}
                        <span className="ml-1 text-sm capitalize">{peer.status}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex space-x-2">
                    <button 
                      onClick={() => downloadPeerConfig(peer)}
                      className="p-2 text-slate-400 hover:text-slate-200"
                      title="Download configuration"
                    >
                      <Download className="w-4 h-4" />
                    </button>
                    <button 
                      onClick={() => deletePeer(peer)}
                      disabled={deletingPeer === peer.publicKey}
                      className="p-2 text-slate-400 hover:text-red-400 disabled:opacity-50"
                      title="Delete peer"
                    >
                      <Trash2 className={`w-4 h-4 ${deletingPeer === peer.publicKey ? 'animate-pulse' : ''}`} />
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
                  <div>
                    <span className="text-slate-400">Allowed IPs:</span>
                    <div className="text-slate-200 font-mono">{peer.allowedIPs}</div>
                  </div>
                  <div>
                    <span className="text-slate-400">Endpoint:</span>
                    <div className="text-slate-200 font-mono">{peer.endpoint}</div>
                  </div>
                  <div>
                    <span className="text-slate-400">Last Handshake:</span>
                    <div className="text-slate-200">{peer.lastHandshake}</div>
                  </div>
                  <div>
                    <span className="text-slate-400">Transfer:</span>
                    <div className="text-slate-200">↓{peer.transferRx} ↑{peer.transferTx}</div>
                  </div>
                </div>

                {/* Public Key */}
                <div className="mt-3 pt-3 border-t border-slate-600">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-slate-400 text-sm">Public Key:</span>
                    <button 
                      onClick={() => copyToClipboard(peer.publicKey)}
                      className="text-slate-400 hover:text-slate-200"
                    >
                      <Copy className="w-3 h-3" />
                    </button>
                  </div>
                  <div className="bg-slate-800 rounded p-2 font-mono text-xs text-slate-300 break-all">
                    {peer.publicKey}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Add Peer Modal */}
      {showAddPeer && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-slate-800 border border-slate-700 rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold text-slate-200 mb-4">Add New Peer</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Peer Name
                </label>
                <input
                  type="text"
                  value={newPeer.name}
                  onChange={(e) => setNewPeer({...newPeer, name: e.target.value})}
                  className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-md text-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., John's Laptop"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Allowed IPs
                </label>
                <input
                  type="text"
                  value={newPeer.allowedIPs}
                  onChange={(e) => setNewPeer({...newPeer, allowedIPs: e.target.value})}
                  className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-md text-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., 10.8.0.5/32"
                />
              </div>
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => setShowAddPeer(false)}
                className="px-4 py-2 text-slate-400 hover:text-slate-200"
              >
                Cancel
              </button>
              <button
                onClick={handleAddPeer}
                disabled={addingPeer}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                {addingPeer ? 'Adding...' : 'Add Peer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VPNManagement;